import { Component, Inject, ChangeDetectionStrategy } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatDialog } from "@angular/material/dialog";
import { AnalyticalMethodService } from "src/app/services/analytical-method.service";
import { FLYOUT_DATA } from "src/app/shared/flyout/flyout-container/flyout-data.token";
import { ToastService } from "src/app/services/toast.service";
import { AnalyticalMethod } from "src/app/models/analyticalMethod.model";
import { AgentJurisdictionService } from "src/app/services/agent-jurisdiction.service";
import { AgentJurisdiction } from "src/app/models/agent-jurisdiction.model";
import { Lookup } from "src/app/models/lookup.model";
import { forkJoin } from "rxjs";
import { AgentJurisdictionDialogComponent } from "src/app/modals/agent-jurisdiction-dialog/agent-jurisdiction-dialog.component";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import { MediaToAnalyticalMethodDialogComponent } from "src/app/media-to-analytical-method-dialog/media-to-analytical-method-dialog.component";
import { TranslateService } from "src/app/services/translate.service";

@Component({
  standalone: false,
    selector: "app-analytical-method-details-popup",
    templateUrl: "./analytical-method-details-popup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./analytical-method-details-popup.component.scss"],
})
export class AnalyticalMethodDetailsPopupComponent {
    associatedMediaColumns: string[] = ["associatedMedia", "actions"];

    agentJurisdictionColumns: string[] = ["name", "actions"];

    associatedMediaDataSource = new MatTableDataSource<string>([]);
    agentJurisdictionDataSource = new MatTableDataSource<Lookup>([]);
    isLoading: boolean;
    analyticalMethodDetails: AnalyticalMethod;

    constructor(
        @Inject(FLYOUT_DATA) public data: string,
        private dialog: MatDialog,
        private analyticalMethodService: AnalyticalMethodService,
        private agentJurisdictionService: AgentJurisdictionService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {
        if (data) {
            this.loadAnalyticalMethodData();
        }
    }

    loadAnalyticalMethodData() {
        this.isLoading = true;
        const filter: Partial<AgentJurisdiction> = {
            analyticalMethodId: this.data,
        };
        forkJoin({
            agentJurisdiction: this.agentJurisdictionService.getAll(
                JSON.stringify(filter),
            ),
            analyticalMethod: this.analyticalMethodService.getById(this.data),
        }).subscribe({
            next: ({ agentJurisdiction, analyticalMethod }) => {
                this.analyticalMethodDetails = analyticalMethod;
                this.agentJurisdictionDataSource.data = agentJurisdiction;
                this.associatedMediaDataSource.data =
                    analyticalMethod.sampleMediaIds;
                this.isLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            },
        });
    }

    deleteSampleMedia(sampleMediaId: string): void {
        const remainingMediaIds =
            this.analyticalMethodDetails.sampleMediaIds.filter(
                (id) => id !== sampleMediaId,
            );
        const dialogRef = this.dialog.open(DeleteDialog, {
            data: {
                id: this.analyticalMethodDetails.id,
                formDeletes: true,
                title: this.translateService.getDynamicTranslation(
                    "Sample Media",
                ),
                customDelete: () => {
                    const data = {
                        ...this.analyticalMethodDetails,
                        sampleMediaIds: remainingMediaIds,
                    };
                    return this.analyticalMethodService.update(data);
                },
            },
            width: "440px",
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === true) {
                this.removeSampleMediaRow(sampleMediaId);
            }
        });
    }

    removeSampleMediaRow(id: string): void {
        const index = this.associatedMediaDataSource.data.findIndex(
            (item) => item === id,
        );
        if (index > -1) {
            this.associatedMediaDataSource.data.splice(index, 1);
            this.associatedMediaDataSource._updateChangeSubscription();
        }
    }

    addSampleMedia() {
        const dialogRef = this.dialog.open(
            MediaToAnalyticalMethodDialogComponent,
            {
                width: "600px",
                disableClose: true,
                data: this.analyticalMethodDetails,
            },
        );

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.loadAnalyticalMethodData();
            }
        });
    }

    agentJurisdictionDialog(id: string): void {
        const dialogRef = this.dialog.open(AgentJurisdictionDialogComponent, {
            data: { id: id },
            width: "600px",
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.updateAgentJurisdictionRow(result.id);
            }
        });
    }

    updateAgentJurisdictionRow(id: string): void {
        const index = this.agentJurisdictionDataSource.data.findIndex(
            (item) => item.id === id,
        );

        if (index > -1) {
            this.associatedMediaDataSource.data[index] = id;
            this.associatedMediaDataSource._updateChangeSubscription();
        }
    }
}
